import React from 'react'
import Layout1 from 'app/components/MatxLayout/Layout1/Layout1'
import { Store } from 'app/redux/Store'
import { PERMISSION_MODE } from 'app/ducks/auth/types'
import moment from 'moment'

export const isLoggedUserSuperuser = () => {
  const { auth } = Store.getState()
  return !!auth?.userInfo.is_superuser
}

export const isLoggedUserStaff = () => {
  const { auth } = Store.getState()
  return !!auth?.userInfo.is_staff
}

export const isFromType = (type) => {
  const { auth } = Store.getState()
  return auth?.userInfo.user_type === type
}

export const isAuthenticated = () => {
  const { auth } = Store.getState()
  return !!auth?.token
}

export const UserRoles = {
  ANY: 'any',
  SUPERUSER: 'superuser',
  STAFF: 'staff',
  ASSOCIATE: 'associate',
  DIRECTOR: 'director',
  RELATIVE: 'relative',
  RESPONSIBLE: 'responsible',
  MEMBER: 'member',
  COLLABORATOR: 'collaborator',
  BIOMETRY_MANAGER: 'biometry_manager',
  FINANCIAL: 'financial',
  ADMINISTRATOR: 'administrator',
  ANY_AUTHENTICATED: 'anyAuthenticated',
}

export const rolesAccessRules = {
  any: () => true,
  superuser: isLoggedUserSuperuser,
  staff: isLoggedUserStaff,
  totem: () => isFromType('totem'),
  associate: () => isFromType('associate'),
  director: () => isFromType('director'),
  relative: () => isFromType('relative'),
  responsible: () => isFromType('responsible'),
  member: () => isFromType('member'),
  collaborator: () => isFromType('collaborator'),
  biometry_manager: () => isFromType('biometry_manager'),
  manager: () => isFromType('manager'),
  financial: () => isFromType('financial'),
  administrator: () => isFromType('administrator'),
  partner_company: () => isFromType('partner_company'),
  anyAuthenticated: isAuthenticated,
}

// One wrapper per page component: a fresh function on every call would be a
// new component type on every render of the route, remounting the whole
// layout and page (losing input focus, e.g. in the list search) whenever
// the URL changes.
const wrappedLayouts = new WeakMap()

export const wrapLayout = (Component) => {
  if (!wrappedLayouts.has(Component)) {
    wrappedLayouts.set(Component, () => (
      <Layout1>
        <Component />
      </Layout1>
    ))
  }
  return wrappedLayouts.get(Component)
}

export const isUserInRole = (roles) =>
  !!roles?.some((role) => rolesAccessRules[role]?.())

/**
 * Imperative counterpart to the `useHasCapability` hook. See its docstring
 * for the full semantics. Use the hook inside components; use this helper
 * from event handlers, routers, or other code that already pulls state
 * from `Store` imperatively.
 *
 * @param {string} capability `app_label.codename` perm key.
 * @param {{legacyRoles?: string[]|string}} [options]
 * @returns {boolean}
 */
export const hasCapability = (capability, options = {}) => {
  const { legacyRoles } = options
  const { auth } = Store.getState()
  const info = auth?.userInfo
  if (!info) return false

  if (info.permission_mode === PERMISSION_MODE.DJANGO) {
    if (!capability) return false
    return !!auth?.permissions?.includes(capability)
  }

  if (!legacyRoles) return false
  if (typeof legacyRoles === 'string') return info.user_type === legacyRoles
  if (Array.isArray(legacyRoles)) return legacyRoles.includes(info.user_type)
  throw new Error(`Invalid value ${legacyRoles} for legacyRoles`)
}

export const isUserAboveAge = (age) => {
  const { auth } = Store.getState()
  const birthDate = auth?.userInfo.birth_date
  return (
    !!birthDate &&
    moment.duration(moment().diff(moment(birthDate))).asYears() > age
  )
}
